import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Download, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 25;

export default function AdminDashboard() {
  const [data, setData] = useState({ items: [], total: 0, total_pages: 0 });
  const [stats, setStats] = useState({ total: 0, new: 0, shortlisted: 0, accepted: 0 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter]);

  useEffect(() => {
    let cancelled = false;
    const fetchApplications = async () => {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      try {
        const params = { page, page_size: PAGE_SIZE };
        if (debouncedSearch) params.search = debouncedSearch;
        if (statusFilter !== 'all') params.status = statusFilter;
        const response = await axios.get(`${BACKEND_URL}/api/applications`, {
          headers: { Authorization: `Bearer ${token}` },
          params,
        });
        if (!cancelled) setData(response.data);
      } catch (error) {
        console.error('Error fetching applications:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchApplications();
    return () => { cancelled = true; };
  }, [page, debouncedSearch, statusFilter]);

  // Fetch lightweight stats once (all + each status count) using paginated total
  useEffect(() => {
    const fetchStats = async () => {
      const token = localStorage.getItem('adminToken');
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [all, fresh, shortlisted, accepted] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/applications`, { headers, params: { page: 1, page_size: 1 } }),
          axios.get(`${BACKEND_URL}/api/applications`, { headers, params: { page: 1, page_size: 1, status: 'new' } }),
          axios.get(`${BACKEND_URL}/api/applications`, { headers, params: { page: 1, page_size: 1, status: 'shortlisted' } }),
          axios.get(`${BACKEND_URL}/api/applications`, { headers, params: { page: 1, page_size: 1, status: 'accepted' } }),
        ]);
        setStats({
          total: all.data.total,
          new: fresh.data.total,
          shortlisted: shortlisted.data.total,
          accepted: accepted.data.total,
        });
      } catch (err) {
        console.error('Stats error:', err);
      }
    };
    fetchStats();
  }, [data.total]);

  const handleExport = async () => {
    const token = localStorage.getItem('adminToken');
    try {
      const response = await axios.get(`${BACKEND_URL}/api/applications/export/csv`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'applications.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error exporting:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-500';
      case 'reviewing': return 'bg-yellow-500';
      case 'shortlisted': return 'bg-purple-500';
      case 'accepted': return 'bg-green-500';
      case 'rejected': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const apps = data.items;

  return (
    <div data-testid="admin-dashboard">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-4xl font-unbounded font-black" data-testid="dashboard-title">Applications</h1>
        <Button
          onClick={handleExport}
          className="bg-accent text-white px-6 py-3 font-bold uppercase"
          data-testid="export-button"
        >
          <Download size={20} className="mr-2" />
          Export CSV
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-card p-6 border border-border">
          <p className="text-sm font-manrope text-gray-400 mb-2">Total Applications</p>
          <p className="text-3xl font-unbounded font-black">{stats.total}</p>
        </div>
        <div className="bg-card p-6 border border-border">
          <p className="text-sm font-manrope text-gray-400 mb-2">New</p>
          <p className="text-3xl font-unbounded font-black">{stats.new}</p>
        </div>
        <div className="bg-card p-6 border border-border">
          <p className="text-sm font-manrope text-gray-400 mb-2">Shortlisted</p>
          <p className="text-3xl font-unbounded font-black">{stats.shortlisted}</p>
        </div>
        <div className="bg-card p-6 border border-border">
          <p className="text-sm font-manrope text-gray-400 mb-2">Accepted</p>
          <p className="text-3xl font-unbounded font-black">{stats.accepted}</p>
        </div>
      </div>

      <div className="bg-card p-6 border border-border mb-6">
        <div className="flex gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <Input
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-48" data-testid="status-filter">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="reviewing">Reviewing</SelectItem>
              <SelectItem value="shortlisted">Shortlisted</SelectItem>
              <SelectItem value="accepted">Accepted</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <p className="text-center font-manrope">Loading applications...</p>
      ) : apps.length === 0 ? (
        <div className="bg-card p-12 border border-border text-center">
          <p className="font-manrope text-gray-400">No applications found.</p>
        </div>
      ) : (
        <>
          <div className="bg-card border border-border overflow-hidden">
            <table className="w-full" data-testid="applications-table">
              <thead className="bg-muted">
                <tr>
                  <th className="px-6 py-4 text-left font-manrope font-bold">Name</th>
                  <th className="px-6 py-4 text-left font-manrope font-bold">Email</th>
                  <th className="px-6 py-4 text-left font-manrope font-bold">City</th>
                  <th className="px-6 py-4 text-left font-manrope font-bold">Status</th>
                  <th className="px-6 py-4 text-left font-manrope font-bold">Applied</th>
                  <th className="px-6 py-4 text-left font-manrope font-bold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {apps.map((app) => (
                  <tr key={app.id} className="border-t border-border hover:bg-muted/50" data-testid={`application-row-${app.id}`}>
                    <td className="px-6 py-4 font-manrope">{app.full_name}</td>
                    <td className="px-6 py-4 font-manrope text-gray-400">{app.email}</td>
                    <td className="px-6 py-4 font-manrope">{app.city_preference}</td>
                    <td className="px-6 py-4">
                      <span className={`${getStatusColor(app.status)} text-white px-3 py-1 text-xs font-manrope font-medium uppercase`}>
                        {app.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 font-manrope text-gray-400">
                      {new Date(app.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      <Link
                        to={`/admin/applications/${app.id}`}
                        className="text-accent hover:underline font-manrope font-medium"
                        data-testid={`view-application-${app.id}`}
                      >
                        View Details
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between mt-6" data-testid="pagination">
            <div className="font-manrope text-sm text-gray-400">
              Showing {apps.length} of {data.total} applications
            </div>
            {data.total_pages > 1 && (
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page <= 1}
                  className="flex items-center gap-1 px-4 py-2 border border-border font-manrope disabled:opacity-40"
                  data-testid="pagination-prev"
                >
                  <ChevronLeft size={16} /> Prev
                </button>
                <span className="font-manrope text-gray-400">
                  Page {page} of {data.total_pages}
                </span>
                <button
                  onClick={() => setPage(Math.min(data.total_pages, page + 1))}
                  disabled={page >= data.total_pages}
                  className="flex items-center gap-1 px-4 py-2 border border-border font-manrope disabled:opacity-40"
                  data-testid="pagination-next"
                >
                  Next <ChevronRight size={16} />
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
